import { useEffect, useMemo, useRef, useState } from "react";
import { tempoChanges } from "../core/grid/fixedGrid";
import { useControllers } from "../state/controllers";
import { usePlaybackStore, METRO_OFFSET_MAX } from "../state/playbackStore";
import { useProjectStore } from "../state/projectStore";
import { VolumeIcon, TargetIcon, MenuIcon, MetronomeIcon, BookmarkIcon } from "./icons";
import { useT, useLang, LANGS } from "../i18n";
import { useAuth, useLinks, renderGoogleButton } from "../state/auth";
import { useUi } from "../state/ui";

// Topbar controls: save-link toggle + ⚙ settings popover (account, volume, cursor compensation, beat
// level, metronome, language). Play lives in the bottom PlayDock.
export function PlaybackControls({ bandMode = false }: { bandMode?: boolean }) {
  const c = useControllers();
  const volume = usePlaybackStore((s) => s.volume);
  const cursorComp = usePlaybackStore((s) => s.cursorComp);
  const setVolume = usePlaybackStore((s) => s.setVolume);
  const setCursorComp = usePlaybackStore((s) => s.setCursorComp);
  const metroOffset = usePlaybackStore((s) => s.metroOffset);
  const setMetroOffset = usePlaybackStore((s) => s.setMetroOffset);
  const metroGrid = usePlaybackStore((s) => s.metroGrid);
  const setMetroGrid = usePlaybackStore((s) => s.setMetroGrid);
  const tempoGrid = useProjectStore((s) => s.tempoGrid);
  const changes = useMemo(() => tempoChanges(tempoGrid), [tempoGrid]);

  const beatLevelMul = useProjectStore((s) => s.beatLevelMul);
  const setBeatLevelMul = useProjectStore((s) => s.setBeatLevelMul);
  const t = useT();
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const gbtnRef = useRef<HTMLDivElement>(null);
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const openLinks = useUi((s) => s.setLinksPage);
  const source = useProjectStore((s) => s.source);
  const videoId = source.kind === "youtube" ? source.videoId : null;
  const saved = useLinks((s) => !!videoId && s.links.some((l) => l.vid === videoId));
  const [linkMsg, setLinkMsg] = useState("");

  // render the Google sign-in button when the menu is open and the user is logged out
  useEffect(() => {
    if (open && !user && gbtnRef.current) renderGoogleButton(gbtnRef.current).catch(() => {});
  }, [open, user]);

  // bookmark: save / unsave this YouTube song on the account (logged out → the menu's sign-in)
  const toggleLink = async () => {
    if (!videoId) return;
    if (!user) { setOpen(true); return; }
    if (saved) { await useLinks.getState().remove(videoId); setLinkMsg(t("links.removed")); }
    else setLinkMsg((await useLinks.getState().save(videoId)) ? t("links.saved") : t("links.fail"));
    setTimeout(() => setLinkMsg(""), 2500);
  };

  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="transport">
      {!bandMode && videoId && (
        <button className={"settings-btn link-btn" + (saved ? " on" : "")} onClick={toggleLink}
          title={saved ? t("links.unsave") : t("links.save")} aria-label={saved ? t("links.unsave") : t("links.save")}
          aria-pressed={saved}>
          <BookmarkIcon size={18} filled={saved} />
        </button>
      )}
      {!bandMode && linkMsg && <div className="link-toast" role="status">{linkMsg}</div>}
      <div className="settings-wrap" ref={wrapRef}>
        <button
          className={"settings-btn menu-btn" + (open ? " on" : "")}
          onClick={() => setOpen((o) => !o)}
          title={t("ctrl.settings")} aria-label={t("ctrl.settings")} aria-expanded={open}
        >
          <MenuIcon size={22} />
        </button>

        {open && (
          <div className="settings-menu" role="menu">
            <div className="settings-acct">
              <span className="ctrl-label">{t("acct.account")}</span>
              {user ? (
                <>
                  <div className="acct-user">{user.name}</div>
                  <div className="acct-actions">
                    {!bandMode && <button className="acct-btn" onClick={() => { openLinks(true); setOpen(false); }}>{t("acct.mylinks")}</button>}
                    <button className="acct-btn ghost" onClick={() => logout()}>{t("acct.logout")}</button>
                  </div>
                </>
              ) : (
                <>
                  <div ref={gbtnRef} className="gbtn" />
                  <div className="acct-hint">{t("acct.login_hint")}</div>
                </>
              )}
            </div>
            {!bandMode && <>
            <div className="settings-divider" />
            <div className="settings-row">
              <span className="ctrl-label"><VolumeIcon size={15} /> {t("ctrl.volume")}</span>
              <input type="range" min={0} max={100} value={volume}
                style={{ ["--p" as string]: `${volume}%` }}
                onChange={(e) => { const v = +e.target.value; setVolume(v); c.song.setVolume(v); c.youtube.setVolume(v); }} />
              <span className="ctrl-val">{volume}</span>
            </div>
            <div className="settings-row">
              <span className="ctrl-label"><TargetIcon size={15} /> {t("ctrl.cursorcomp")}</span>
              <input type="range" min={0} max={300} step={10} value={cursorComp}
                style={{ ["--p" as string]: `${(cursorComp / 300) * 100}%` }}
                onChange={(e) => { const ms = +e.target.value; setCursorComp(ms); c.song.setCursorComp(ms / 1000); c.metro.setCursorComp(ms / 1000); }} />
              <span className="ctrl-val">{cursorComp}ms</span>
            </div>
            {hasChart && (
              <div className="settings-lang">
                <span className="ctrl-label">{t("ctrl.beatlevel")}</span>
                <div className="lang-seg">
                  {([0.5, 1, 2] as const).map((m) => (
                    <button key={m} className={"lang-opt" + (beatLevelMul === m ? " on" : "")} onClick={() => setBeatLevelMul(m)}>
                      {m === 0.5 ? "×½" : m === 2 ? "×2" : "×1"}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="settings-lang">
              <span className="ctrl-label"><MetronomeIcon size={15} /> {t("metro.grid")}</span>
              <div className="lang-seg">
                <button className={"lang-opt" + (metroGrid === "fixed" ? " on" : "")} onClick={() => setMetroGrid("fixed")}>
                  {t("metro.grid_fixed")}
                  {changes.length > 1 ? ` · ${t("metro.blocks", { n: changes.length })}` : tempoGrid ? ` ${tempoGrid.bpm.toFixed(1)}` : ""}
                </button>
                <button className={"lang-opt" + (metroGrid === "detected" ? " on" : "")} onClick={() => setMetroGrid("detected")}>
                  {t("metro.grid_detected")}
                </button>
              </div>
            </div>
            <div className="settings-row">
              <span className="ctrl-label"><MetronomeIcon size={15} /> {t("ctrl.metrooffset")}</span>
              <input type="range" min={-METRO_OFFSET_MAX} max={METRO_OFFSET_MAX} step={5} value={metroOffset}
                aria-label={t("ctrl.metrooffset")}
                style={{ ["--p" as string]: `${((metroOffset + METRO_OFFSET_MAX) / (2 * METRO_OFFSET_MAX)) * 100}%` }}
                onChange={(e) => setMetroOffset(+e.target.value)} />
              <span className="ctrl-val">{metroOffset > 0 ? "+" : ""}{metroOffset}ms</span>
            </div>
            <div className="settings-hint">{t("ctrl.metrooffset_hint")}</div>
            <div className="settings-lang">
              <span className="ctrl-label">{t("ctrl.language")}</span>
              <div className="lang-seg">
                {LANGS.map((l) => (
                  <button key={l.code} className={"lang-opt" + (lang === l.code ? " on" : "")}
                    onClick={() => setLang(l.code)}>{l.label}</button>
                ))}
              </div>
            </div>
            </>}
            <div className="settings-divider" />
            <nav className="settings-nav">
              {!bandMode && <a className="nav-link" href="/popular">{t("footer.popular")}</a>}
              <a className="nav-link" href="/about">{t("footer.about")}</a>
              <a className="nav-link" href="/contact">{t("footer.contact")}</a>
              <a className="nav-link" href="/terms">{t("footer.terms")}</a>
              <a className="nav-link" href="/privacy">{t("footer.privacy")}</a>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
